import { trpc } from '@web/lib/trpc';
import { useCallback, useEffect } from 'react';
import { useMeasure } from 'react-use';

import { toast } from 'sonner';

import { MessageAPI } from '@shared/types';

import { useContacts } from '@web/hooks';

import { Card } from '@web/components/ui/card';

import { ContactsList } from './contacts-list';
import { ChatHeader } from './chat-header';
import { MessagesArea } from './messages-area';

import { useAuthContext } from '@web/auth';

import { Route } from '@web/routes/dashboard/chat/index';
import { PaymentCollector } from '@web/payments/components/payment-collector';
import ContractEditor from '@web/contracts/components/create-contract-form/contract-editor';
import { CommentAddOrEditDialog } from './comment-add-or-edit';
import { EditContactDialog } from './edit-contact-dialog';
import { CreateContactDialog } from './create-contact-dialog';

export function ChatInterface() {
  const {
    activeContractId,
    setActiveContractId,
    activePaymentContractId,
    setActivePaymentContractId,
    activeCommentId,
    setActiveCommentId,
    contactInfoSidebarContactId,
    setContactInfoSidebarContactId,
    editContactDialogContactId,
    setEditContactDialogContactId,
    createContactDialogOpen,
    setCreateContactDialogOpen,
  } = Route.useRouteContext();

  const {
    messaging: { getContacts },
  } = trpc.useUtils();

  const { user } = useAuthContext();
  const userId = user?.id ?? '';

  const navigate = Route.useNavigate();

  const { id: activeContactId } = Route.useSearch();

  const {
    messaging: { getMessages: getMessagesUtils, getContacts: getContactsUtils },
  } = trpc.useUtils();

  const { contacts } = useContacts();

  const setActiveContactId = useCallback(
    (id: string) => {
      // If id is empty string, we're clearing the contact selection (going back to list)
      if (id === '') {
        void navigate({
          search: {}, // Clear all search params including id
          replace: true // Replace current URL to avoid creating extra history entries
        });
      } else {
        void navigate({
          search: { id },
        });
      }
    },
    [navigate],
  );

  const addMessage = (message: MessageAPI) => {
    const newMessage = {
      ...message,
      isReadByUser:
        message.contactId === activeContactId ? true : message.isReadByUser,
    };

    getMessagesUtils.setInfiniteData(
      { contactId: newMessage.contactId, userId: message.userId },
      (prevData) => {
        if (!prevData) return { pageParams: [], pages: [] };

        const { pages, pageParams } = prevData;
        const firstPageFull = prevData.pages[0].length >= 20;

        if (firstPageFull)
          return {
            pageParams: [{}, ...pageParams],
            pages: [[newMessage], ...pages],
          };

        return {
          pageParams,
          pages: pages.map((page, idx) =>
            idx === 0 ? [newMessage, ...page] : page,
          ),
        };
      },
    );
  };

  trpc.messaging.onNewMessage.useSubscription(
    { contactId: null },

    {
      onData: ({ data: newMessage }) => {
        addMessage(newMessage);

        getContactsUtils.setInfiniteData(
          {},

          (prevData) => ({
            pageParams: prevData?.pageParams ?? [],

            pages:
              prevData?.pages.map((page) =>
                page.map((contact) =>
                  contact.id === newMessage.contactId
                    ? {
                        ...contact,
                        lastMessageDate: newMessage.timestamp,
                      }
                    : contact,
                ),
              ) ?? [],
          }),
        );

        const contact = contacts.find(({ id }) => id === newMessage.contactId);
        if (contact && contact.id !== activeContactId)
          toast.message(`Message from ${contact.name}`, {
            description: newMessage.content,
          });
      },
    },
  );

  trpc.messaging.onMessageStatusUpdated.useSubscription(
    {},
    {
      onData: (update) => {
        getMessagesUtils.setInfiniteData(
          { contactId: update.contactId, userId: update.userId },
          (prevData) => ({
            pageParams: prevData?.pageParams ?? [],
            pages:
              prevData?.pages.map((page) =>
                page.map((message) =>
                  update.communicationId === message.id
                    ? {
                        ...message,
                        status: update.newStatus,
                      }
                    : message,
                ),
              ) ?? [],
          }),
        );
      },
    },
  );

  trpc.messaging.onReplySuggestions.useSubscription(
    { contactId: null },

    {
      onData: (replySuggestionsData) => {
        getMessagesUtils.setInfiniteData(
          { contactId: replySuggestionsData.contactId, userId },
          (prevData) => {
            if (!prevData) return;

            return {
              pageParams: prevData.pageParams,
              pages: prevData.pages.map((page) =>
                page.map((message) =>
                  message.id.toString() === replySuggestionsData.parentMessageId
                    ? {
                        ...message,
                        replySuggestions: replySuggestionsData.messages,
                      }
                    : message,
                ),
              ),
            };
          },
        );
      },
    },
  );

  const { mutateAsync: markAllMessagesAsReadAsync } =
    trpc.messaging.markAllMessagesAsRead.useMutation();

  const markAllMessagesAsRead = useCallback(
    async (contactId: string) => {
      await markAllMessagesAsReadAsync({ contactId });

      getMessagesUtils.setInfiniteData(
        { contactId, userId },

        (prevData) => {
          if (!prevData) return undefined;

          return {
            ...prevData,

            pages: prevData.pages.map((page) => {
              return page.map((message) => {
                return {
                  ...message,
                  isReadByUser: true,
                };
              });
            }),
          };
        },
      );
    },
    [getMessagesUtils, markAllMessagesAsReadAsync, userId],
  );

  useEffect(() => {
    if (activeContactId) void markAllMessagesAsRead(activeContactId);
  }, [activeContactId, markAllMessagesAsRead]);

  useEffect(() => {
    if (activeContactId && contactInfoSidebarContactId) {
      setContactInfoSidebarContactId(activeContactId);
    }

    if (!activeContactId && contactInfoSidebarContactId) {
      setContactInfoSidebarContactId(null);
    }
  }, [
    activeContactId,
    contactInfoSidebarContactId,
    setContactInfoSidebarContactId,
  ]);

  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const isMobileView = width <= 640;

  return (
    <>
      <Card ref={ref} className="flex h-full w-full overflow-hidden p-0">
        {/* Show contacts list on desktop (width > 800) or when no active contact on mobile */}
        {(width > 800 || (isMobileView && !activeContactId)) && (
          <ContactsList
            activeContactId={activeContactId}
            setActiveContactId={setActiveContactId}
            isMobileView={isMobileView}
          />
        )}

        {/* Show messages area when there's an active contact */}
        {activeContactId !== undefined && (
          <div className="flex flex-1 flex-col">
            <ChatHeader 
              activeContactId={activeContactId} 
              isMobileView={isMobileView}
              onBackToList={() => {
                // Clear the contact ID from URL and show the contacts list
                setActiveContactId('');
              }} 
            />

            <MessagesArea contactId={activeContactId} />
          </div>
        )}
      </Card>

      {activeContractId !== null && (
        <ContractEditor
          key={activeContractId}
          contractId={activeContractId}
          onClose={() => {
            setActiveContractId(null);
          }}
        />
      )}

      {activeContactId !== undefined && activePaymentContractId !== null && (
        <PaymentCollector
          key={activePaymentContractId}
          contactId={activeContactId}
          contractId={activePaymentContractId}
          onClose={() => {
            setActivePaymentContractId(null);
          }}
        />
      )}

      {activeContactId !== undefined && activeCommentId !== null && (
        <CommentAddOrEditDialog
          key={activeCommentId}
          commentId={activeCommentId}
          onClose={() => {
            setActiveCommentId(null);
          }}
        />
      )}

      {editContactDialogContactId !== null && (
        <EditContactDialog
          key={editContactDialogContactId}
          contactId={editContactDialogContactId}
          onClose={() => {
            setEditContactDialogContactId(null);

            void getContacts.refetch();
          }}
        />
      )}

      {createContactDialogOpen && (
        <CreateContactDialog
          onClose={() => {
            setCreateContactDialogOpen(false);
            void getContacts.refetch();
          }}
        />
      )}
    </>
  );
}
